/**
 * On the person — **Sells** and **Working day** (`docs/new-request.md` §14.2,
 * Board 12 A and B).
 *
 * Two cards at the top of a person's Profile tab and the one 480 sheet both
 * open. Sells is the same rows as Settings → Sales coverage (a branch is the
 * person's row on that branch's grid, a tick is an R or a C under a division),
 * so a change here is a change there. The working day is where the day starts
 * and ends and which hours it holds — what drive times and openings are worked
 * out from.
 *
 * `GET /selling/people/:id` says what the caller may change, so the Edit
 * buttons follow the server rather than a second copy of the rule: somebody
 * may edit their own day without being able to touch what they sell.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { SEGMENTS, SEGMENT_LABELS, clock, minutesOfTime, type Segment } from '@rowted/shared'
import { Sheet } from '../sheet.js'
import { Avatar, Band, Button, Checkbox, Segmented } from '../primitives.js'
import { Field } from '../form/field.js'
import { SelectInput } from '../form/inputs.js'
import { MenuButton } from '../menu-select.js'
import { AddressAutocomplete } from '../form/address-autocomplete.js'
import { IconCheck, IconClose, IconEdit, IconPlus } from '../../frame/icons.js'
import { request } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'

// ---------------------------------------------------------------------------
// The payload — `server/src/routes/selling.ts`
// ---------------------------------------------------------------------------

type DayStartsAt = 'branch' | 'home' | 'address'

interface DayAddress { line1: string; line2: string; city: string; state: string; postalCode: string }

export interface PersonSelling {
  person: { id: string; name: string; initials: string; homeBranch: { id: string; name: string } | null }
  canEditSells: boolean
  canEditDay: boolean
  sells: {
    branches: { id: string; name: string; editable: boolean }[]
    ticks: { orgUnitId: string; divisionId: string; segment: Segment }[]
    covers: { divisionId: string; segment: Segment; label: string }[]
  }
  divisions: { id: string; code: string; name: string }[]
  branchOptions: { id: string; name: string }[]
  day: {
    startsAt: DayStartsAt
    address: DayAddress | null
    latitude: number | null
    longitude: number | null
    addressHidden: boolean
    startsFrom: { title: string; line: string | null }
    workDays: number[]
    workStart: string
    workEnd: string
  }
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** `HH:MM` to the board's *8:00 AM*. */
const clockOf = (time: string) => clock(minutesOfTime(time))

/** Every quarter hour of the day, for From and To. */
const TIMES = Array.from({ length: 96 }, (_, index) => {
  const minutes = index * 15
  const value = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
  return { value, label: clock(minutes) }
})

// ---------------------------------------------------------------------------
// The two cards (Board 12 A)
// ---------------------------------------------------------------------------

/** The cards and the sheet, for one person. Draws nothing for a caller who may not read them. */
export function PersonSellingCards({ userId, className }: {
  userId: string
  /** The band the two cards sit on (Board 12 A draws them on the page grey). */
  className?: string
}) {
  const [data, setData] = useState<PersonSelling | null>(null)
  const [hidden, setHidden] = useState(false)
  const [editing, setEditing] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await request<PersonSelling>(`/selling/people/${userId}`))
      setHidden(false)
    } catch {
      setHidden(true)
    }
  }, [userId])

  useEffect(() => { void load() }, [load])

  if (hidden || !data) return null
  const editable = data.canEditSells || data.canEditDay

  return (
    <div className={cn('flex flex-col gap-3.5', className)}>
      <InfoCard
        title="Sells"
        sub="which requests they’re recommended for"
        onEdit={data.canEditSells ? () => setEditing(true) : undefined}
      >
        <InfoRow label="Branches">
          {data.sells.branches.length === 0
            ? <span className="text-cell text-text-faint">None yet</span>
            : data.sells.branches.map((branch) => <Pill key={branch.id} tone="accent">{branch.name}</Pill>)}
        </InfoRow>
        <InfoRow label="Divisions" top>
          {data.sells.covers.length === 0
            ? <span className="text-cell text-text-faint">None yet</span>
            : data.sells.covers.map((one) => (
              <Pill key={`${one.divisionId}:${one.segment}`}>{one.label}</Pill>
            ))}
        </InfoRow>
      </InfoCard>

      <InfoCard
        title="Working day"
        sub="used to work out drive times and openings"
        onEdit={data.canEditDay ? () => setEditing(true) : undefined}
      >
        <InfoRow label="Starts and ends">
          <span className="text-[13px] text-text">
            <b className="font-bold">{data.day.startsFrom.title}</b>
            {data.day.startsFrom.line && <span> · {data.day.startsFrom.line}</span>}
          </span>
        </InfoRow>
        {/* The days wrap among themselves; the hours stay on their line (Board 12 A). */}
        <InfoRow label="Works" wrap={false}>
          <span className="flex flex-wrap gap-1.5">
            {data.day.workDays.map((day) => (
              <Pill key={day} tone="accent" size="day" icon={<IconCheck size={12} />}>{WEEKDAYS[day - 1]}</Pill>
            ))}
          </span>
          <span className="tnum whitespace-nowrap text-[13px] text-text">
            {clockOf(data.day.workStart)} – {clockOf(data.day.workEnd)}
          </span>
        </InfoRow>
      </InfoCard>

      {editing && editable && (
        <SellingSheet
          data={data}
          onClose={() => setEditing(false)}
          onSaved={(next) => { setData(next); setEditing(false) }}
        />
      )}
    </div>
  )
}

function InfoCard({ title, sub, onEdit, children }: {
  title: string; sub: string; onEdit?: () => void; children: ReactNode
}) {
  return (
    <section className="rounded-record border border-line bg-surface p-[18px]">
      <div className="flex items-center gap-2">
        <h3 className="m-0 text-[14.5px] font-bold text-text">{title}</h3>
        <span className="min-w-0 truncate text-[12px] text-text-muted">{sub}</span>
        {onEdit && (
          <Button className="ml-auto h-[30px] shrink-0 px-[14px] text-[12.5px]" onClick={onEdit}>
            <IconEdit size={14} />Edit
          </Button>
        )}
      </div>
      <dl className="m-0 mt-3 flex flex-col gap-2.5">{children}</dl>
    </section>
  )
}

/**
 * A 90px label and its value, 8px apart (Board 12 A). `top` lines the label
 * up with the first of several wrapping chips; `wrap` off keeps a row's parts
 * — the day chips and the hours — on one line.
 */
function InfoRow({ label, top, wrap = true, children }: {
  label: string; top?: boolean; wrap?: boolean; children: ReactNode
}) {
  return (
    <div className={cn('flex gap-2 max-sm:flex-col max-sm:items-start max-sm:gap-1',
      top ? 'items-start' : 'items-center')}>
      <dt className={cn('w-[90px] shrink-0 text-[12px] text-text-muted', top && 'pt-[5px]')}>{label}</dt>
      <dd className={cn('m-0 flex min-w-0 items-center', wrap ? 'flex-wrap gap-1.5' : 'gap-2')}>
        {children}
      </dd>
    </div>
  )
}

/**
 * A chip that is a fact, not a control: a branch, a division, a working day —
 * 26px, 12.5/600, a branch padded 10 and a division 12; a day is 12px, padded 10.
 */
function Pill({ tone = 'plain', size = 'fact', icon, children }: {
  tone?: 'plain' | 'accent'; size?: 'fact' | 'day'; icon?: ReactNode; children: ReactNode
}) {
  return (
    <span className={cn('inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-chip border',
      'font-semibold',
      size === 'day' ? 'px-2.5 text-[12px]' : cn('text-cell', tone === 'accent' ? 'px-2.5' : 'px-3'),
      tone === 'accent'
        ? 'border-accent-line bg-accent-soft text-accent-dark'
        : 'border-line bg-surface text-text')}>
      {icon}
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// The sheet (Board 12 B)
// ---------------------------------------------------------------------------

interface Draft {
  branchIds: string[]
  ticks: Set<string>
  startsAt: DayStartsAt
  address: DayAddress
  latitude: number | null
  longitude: number | null
  /** Whether the address box was touched, so a withheld address is never sent blank. */
  addressTouched: boolean
  workDays: number[]
  workStart: string
  workEnd: string
}

const tickKey = (divisionId: string, segment: Segment) => `${divisionId}:${segment}`
const EMPTY_ADDRESS: DayAddress = { line1: '', line2: '', city: '', state: '', postalCode: '' }

function draftOf(data: PersonSelling): Draft {
  return {
    branchIds: data.sells.branches.map((one) => one.id),
    /* The union across their branches: Save writes this set to each of them. */
    ticks: new Set(data.sells.ticks.map((one) => tickKey(one.divisionId, one.segment))),
    startsAt: data.day.startsAt,
    address: data.day.address ?? EMPTY_ADDRESS,
    latitude: data.day.latitude,
    longitude: data.day.longitude,
    addressTouched: false,
    workDays: [...data.day.workDays],
    workStart: data.day.workStart,
    workEnd: data.day.workEnd,
  }
}

const sameDraft = (a: Draft, b: Draft) => JSON.stringify({ ...a, ticks: [...a.ticks].sort() })
  === JSON.stringify({ ...b, ticks: [...b.ticks].sort() })

function SellingSheet({ data, onClose, onSaved }: {
  data: PersonSelling
  onClose: () => void
  onSaved: (next: PersonSelling) => void
}) {
  const start = useMemo(() => draftOf(data), [data])
  const [draft, setDraft] = useState<Draft>(start)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = (patch: Partial<Draft>) => setDraft((was) => ({ ...was, ...patch }))

  const branchName = (id: string) => data.sells.branches.find((one) => one.id === id)?.name
    ?? data.branchOptions.find((one) => one.id === id)?.name ?? ''
  const editableBranch = (id: string) => data.branchOptions.some((one) => one.id === id)
  const addable = data.branchOptions.filter((one) => !draft.branchIds.includes(one.id))
  const dirty = !sameDraft(draft, start)

  function toggleTick(divisionId: string, segment: Segment) {
    const next = new Set(draft.ticks)
    const key = tickKey(divisionId, segment)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    set({ ticks: next })
  }

  function toggleDay(day: number) {
    set({ workDays: draft.workDays.includes(day)
      ? draft.workDays.filter((one) => one !== day)
      : [...draft.workDays, day].sort((a, b) => a - b) })
  }

  async function save() {
    const found: Record<string, string> = {}
    if (draft.workStart >= draft.workEnd) found.workEnd = 'The day ends after it starts.'
    if (draft.startsAt !== 'branch' && !data.day.addressHidden && !draft.address.line1.trim()) {
      found.address = draft.startsAt === 'home' ? 'Add the home address.' : 'Add the address.'
    }
    if (data.canEditSells && draft.ticks.size > 0
      && !draft.branchIds.some((id) => editableBranch(id))) {
      found.branches = 'Add a branch — what they sell is per branch.'
    }
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    try {
      const sendAddress = !data.day.addressHidden || draft.addressTouched
      const body = {
        ...(data.canEditSells
          ? {
            sells: {
              branchIds: draft.branchIds.filter((id) => editableBranch(id)),
              ticks: [...draft.ticks].map((key) => {
                const [divisionId, segment] = key.split(':') as [string, Segment]
                return { divisionId, segment }
              }),
            },
          }
          : {}),
        ...(data.canEditDay
          ? {
            day: {
              startsAt: draft.startsAt,
              ...(sendAddress
                ? {
                  address: draft.startsAt === 'branch' && !draft.address.line1.trim()
                    ? null : draft.address,
                  latitude: draft.latitude,
                  longitude: draft.longitude,
                }
                : {}),
              workDays: draft.workDays,
              workStart: draft.workStart,
              workEnd: draft.workEnd,
            },
          }
          : {}),
      }
      onSaved(await request<PersonSelling>(`/selling/people/${data.person.id}`,
        { method: 'PUT', body }))
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      kind="contact"
      mark={<Avatar name={data.person.name} size={34} />}
      title={`${data.person.name} · Sells and working day`}
      sub="the same ticks as Sales coverage"
      width={480}
      dirty={dirty}
      saveLabel="Save"
      onSave={() => void save()}
      primary={(
        <Button variant="primary" className="h-[34px]" disabled={busy} onClick={() => void save()}>
          <IconCheck size={15} />{busy ? 'Saving…' : 'Save'}
        </Button>
      )}
    >
      {problem && <Band tone="amber">{problem}</Band>}

      {data.canEditSells && (
        <>
          <Field label="Branches" group error={errors.branches}>
            {({ labelId }) => (
              <div
                role="group"
                aria-labelledby={labelId}
                className="flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-control border border-line bg-surface p-1.5"
              >
                {draft.branchIds.map((id) => (
                  <span key={id} className={cn('inline-flex h-[26px] items-center gap-1.5 rounded-chip border',
                    'border-accent-line bg-accent-soft pl-2.5 pr-1.5 text-cell font-semibold text-accent-dark')}>
                    {branchName(id)}
                    {editableBranch(id) && (
                      <button
                        type="button"
                        aria-label={`Take ${branchName(id)} off`}
                        className="flex h-[18px] w-[18px] items-center justify-center rounded-full text-accent-dark hover:bg-accent-line"
                        onClick={() => set({ branchIds: draft.branchIds.filter((one) => one !== id) })}
                      >
                        <IconClose size={12} />
                      </button>
                    )}
                  </span>
                ))}
                {addable.length > 0 && (
                  <MenuButton
                    options={addable.map((one) => ({ value: one.id, label: one.name }))}
                    onPick={(id) => set({ branchIds: [...draft.branchIds, id] })}
                    searchPlaceholder="Find a branch…"
                    minWidth={220}
                    className={cn('inline-flex h-[26px] items-center gap-1.5 rounded-chip border border-dashed',
                      'border-accent-line bg-surface px-3 text-cell font-semibold text-accent hover:bg-accent-soft')}
                  >
                    <IconPlus size={13} />Add a branch
                  </MenuButton>
                )}
              </div>
            )}
          </Field>

          <Field label="What they sell" group>
            {({ labelId }) => (
              /* Separate borders with no gap between cells, so the rounded corner clips. */
              <table aria-labelledby={labelId} style={{ borderSpacing: 0 }}
                className="w-full border-separate overflow-hidden rounded-control border border-line text-cell">
                <thead>
                  <tr className="bg-surface-alt text-left text-[11.5px] text-text-muted">
                    <th className="border-b border-line px-3 py-2 font-semibold">Division</th>
                    {SEGMENTS.map((segment) => (
                      <th key={segment} className="w-[90px] border-b border-line px-3 py-2 font-semibold">
                        {SEGMENT_LABELS[segment]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.divisions.map((division, index) => (
                    <tr key={division.id}>
                      <td className={cn('px-3 py-2 text-[13px] text-text',
                        index > 0 && 'border-t border-line-faint')}>
                        {division.name}
                      </td>
                      {SEGMENTS.map((segment) => (
                        <td key={segment} className={cn('px-3 py-2', index > 0 && 'border-t border-line-faint')}>
                          <Checkbox
                            label={`${division.name} · ${SEGMENT_LABELS[segment]}`}
                            hideLabel
                            checked={draft.ticks.has(tickKey(division.id, segment))}
                            onChange={() => toggleTick(division.id, segment)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Field>
        </>
      )}

      {data.canEditDay && (
        <>
          <Field label="The day starts and ends at" group>
            {({ labelId }) => (
              <div className="flex flex-col gap-2">
                <span className="flex">
                  <Segmented<DayStartsAt>
                    label="The day starts and ends at"
                    labelledBy={labelId}
                    size="field"
                    value={draft.startsAt}
                    onChange={(startsAt) => set({ startsAt })}
                    options={[
                      { value: 'branch', label: 'Branch office' },
                      { value: 'home', label: 'Home' },
                      { value: 'address', label: 'Another address' },
                    ]}
                  />
                </span>
              </div>
            )}
          </Field>

          {draft.startsAt !== 'branch' && (
            <Field
              label={draft.startsAt === 'home' ? 'Home address' : 'Address'}
              error={errors.address}
              hint={data.day.addressHidden && !draft.addressTouched
                ? 'kept as it is — type a new one to replace it' : undefined}
            >
              {({ id, describedBy, invalid }) => (
                <AddressAutocomplete
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.address.line1}
                  placeholder="Start typing an address"
                  onChange={(line1) => set({
                    address: { ...draft.address, line1 }, latitude: null, longitude: null,
                    addressTouched: true,
                  })}
                  onChoose={(chosen) => set({
                    address: { line1: chosen.line1, line2: '', city: chosen.city,
                      state: chosen.state, postalCode: chosen.postalCode },
                    latitude: chosen.latitude, longitude: chosen.longitude, addressTouched: true,
                  })}
                />
              )}
            </Field>
          )}

          <Field label="Working hours" group>
            {({ labelId }) => (
              <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((name, index) => {
                  const day = index + 1
                  const on = draft.workDays.includes(day)
                  return (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(day)}
                      className={cn('inline-flex h-7 items-center gap-1.5 rounded-chip border px-2.5',
                        'text-[12px] font-semibold',
                        on
                          ? 'border-accent-line bg-accent-soft text-accent-dark'
                          : 'border-line bg-surface text-text hover:bg-hover')}
                    >
                      {on && <IconCheck size={12} />}
                      {name}
                    </button>
                  )
                })}
              </div>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3.5">
            <Field label="From">
              {({ id, describedBy }) => (
                <SelectInput
                  label="From"
                  id={id}
                  describedBy={describedBy}
                  value={draft.workStart}
                  options={TIMES}
                  onChange={(value) => value && set({ workStart: value })}
                />
              )}
            </Field>
            <Field label="To" error={errors.workEnd}>
              {({ id, describedBy, invalid }) => (
                <SelectInput
                  label="To"
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={draft.workEnd}
                  options={TIMES}
                  onChange={(value) => value && set({ workEnd: value })}
                />
              )}
            </Field>
          </div>
        </>
      )}
    </Sheet>
  )
}
